/** 2D point [x, y]. */
export type Vec2 = [number, number];
/** Row-major 3x3 matrix. */
export type Mat3 = [number, number, number, number, number, number, number, number, number];
/** Corners in order: top-left, top-right, bottom-right, bottom-left. */
export type QuadPoints = [Vec2, Vec2, Vec2, Vec2];

/**
 * Homography mapping the unit square to `quad`:
 * (0,0) -> quad[0], (1,0) -> quad[1], (1,1) -> quad[2], (0,1) -> quad[3].
 * Closed form (Heckbert, "Fundamentals of Texture Mapping", 1989).
 */
export function squareToQuad(quad: QuadPoints): Mat3 {
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = quad;
  const dx1 = x1 - x2;
  const dx2 = x3 - x2;
  const dx3 = x0 - x1 + x2 - x3;
  const dy1 = y1 - y2;
  const dy2 = y3 - y2;
  const dy3 = y0 - y1 + y2 - y3;
  const den = dx1 * dy2 - dx2 * dy1;
  if (Math.abs(den) < 1e-12) throw new Error('Degenerate quad');
  const g = (dx3 * dy2 - dx2 * dy3) / den;
  const h = (dx1 * dy3 - dx3 * dy1) / den;
  return [x1 - x0 + g * x1, x3 - x0 + h * x3, x0, y1 - y0 + g * y1, y3 - y0 + h * y3, y0, g, h, 1];
}

export function invert(m: Mat3): Mat3 {
  const [a, b, c, d, e, f, g, h, i] = m;
  const A = e * i - f * h;
  const B = -(d * i - f * g);
  const C = d * h - e * g;
  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-15) throw new Error('Singular matrix');
  const s = 1 / det;
  return [
    A * s, -(b * i - c * h) * s, (b * f - c * e) * s,
    B * s, (a * i - c * g) * s, -(a * f - c * d) * s,
    C * s, -(a * h - b * g) * s, (a * e - b * d) * s,
  ];
}

/** Applies a homography with the projective divide. */
export function apply(m: Mat3, [x, y]: Vec2): Vec2 {
  const w = m[6] * x + m[7] * y + m[8];
  return [(m[0] * x + m[1] * y + m[2]) / w, (m[3] * x + m[4] * y + m[5]) / w];
}

/** Focal length of a typical phone main camera (26 mm equivalent), as a fraction of the photo's long edge. */
export const PHONE_FOCAL = 0.8;

/**
 * Depth / width of the real rectangle that `quad` (normalized 0..1 on a
 * width x height photo) shows in perspective, assuming a typical phone lens
 * centered on the photo. With H = K [W r1, D r2, t] up to scale, the first two
 * columns of K^-1 H have lengths proportional to W and D.
 */
export function quadAspect(quad: QuadPoints, width: number, height: number, focal = PHONE_FOCAL): number {
  const m = squareToQuad(quad);
  const f = focal * Math.max(width, height);
  const col = (j: number) => {
    const g = m[6 + j];
    // Normalized -> pixels relative to the photo center, then divided by f.
    const x = (m[j] - 0.5 * g) * width / f;
    const y = (m[3 + j] - 0.5 * g) * height / f;
    return Math.hypot(x, y, g);
  };
  return col(1) / col(0);
}
